// Wire protocol between client and server. JSON for the prototype; measured before we
// decide whether binary encoding is worth it (see README metrics).

import type { Input, InputSchema, Meta } from "../contract.js";

export type ClientMsg =
  | { t: "hello"; name?: string; resume?: string }
  | { t: "in"; i: Record<string, unknown> }
  | { t: "ping"; c: number };

/**
 * One entity's changed replicated fields. On the first sight of an entity the patch also
 * carries `_t` (type) and, if owned, `_o` (owner id). Keys starting with "_" are reserved.
 */
export type EntityPatch = Record<string, unknown>;

export interface SnapshotMsg {
  t: "s";
  /** Monotonic per client. */
  seq: number;
  /** Server clock, milliseconds. */
  time: number;
  /** Simulation tick. */
  tick: number;
  /** Your entity id, if you have one. */
  you?: number;
  /** Changed entities, keyed by id. Absent when nothing changed. */
  e?: Record<string, EntityPatch>;
  /** Entities that left your view or were despawned. */
  rm?: number[];
  /** Player names that arrived or left since your last snapshot (null = left). */
  pn?: Record<string, string | null>;
  /** Players in the room. */
  n: number;
  /** Leaderboard: [playerId, score] pairs, best first. Sent when it changes. */
  lb?: [string, number][];
}

export type ServerMsg =
  | { t: "welcome"; id: string; token: string; game: string; room: string; meta: Meta; schema: InputSchema; time: number }
  | SnapshotMsg
  | { t: "pong"; c: number; s: number }
  | { t: "err"; msg: string };

const LIMIT = 1e7;

/** Coerce an untrusted input object to the module's schema. Unknown keys are dropped. */
export function coerceInput(schema: InputSchema, raw: unknown): Input {
  const src = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
  const out: Input = {};
  for (const [k, t] of Object.entries(schema)) {
    const v = src[k];
    if (t === "bool") {
      out[k] = v === true || v === 1;
    } else {
      let n = typeof v === "number" ? v : Number(v);
      if (!Number.isFinite(n)) n = 0;
      n = Math.max(-LIMIT, Math.min(LIMIT, n));
      out[k] = t === "i32" ? n | 0 : Math.round(n * 100) / 100;
    }
  }
  return out;
}

export function parseClientMsg(data: unknown): ClientMsg | null {
  let text: string;
  if (typeof data === "string") text = data;
  else if (data instanceof ArrayBuffer) text = new TextDecoder().decode(data);
  else if (ArrayBuffer.isView(data)) text = new TextDecoder().decode(data as Uint8Array);
  else return null;
  if (text.length > 4096) return null;
  let msg: unknown;
  try {
    msg = JSON.parse(text);
  } catch {
    return null;
  }
  if (!msg || typeof msg !== "object") return null;
  const m = msg as Record<string, unknown>;
  switch (m.t) {
    case "hello":
      return {
        t: "hello",
        name: typeof m.name === "string" ? m.name.slice(0, 24) : undefined,
        resume: typeof m.resume === "string" ? m.resume.slice(0, 64) : undefined,
      };
    case "in":
      return { t: "in", i: m.i && typeof m.i === "object" ? (m.i as Record<string, unknown>) : {} };
    case "ping":
      return typeof m.c === "number" ? { t: "ping", c: m.c } : null;
    default:
      return null;
  }
}
